/**
 * Binary market-data frames (doc 01 §5: "WebSocket + binary frames, little-endian,
 * fixed layout, versioned header").
 *
 * A quote is the highest-frequency message in the system, so it does not travel
 * as JSON. The frame below is a **fixed 96 bytes**: no variable-length section,
 * no per-frame schema lookup, and a client can derive a decoder from this
 * comment without reading the rest of the repository.
 *
 * ```
 * offset  size  type      field            notes
 * ------  ----  --------  ---------------  ------------------------------------
 *   0      2    uint16 LE magic            0x4B57 ('W','K' on the wire: 57 4B)
 *   2      1    uint8     version          1
 *   3      1    uint8     frame_type       1 = QUOTE, 2 = HEARTBEAT
 *   4      2    uint16 LE header_bytes     always 96 (room to grow via version)
 *   6      2    uint16 LE flags            bit0 session_open, bit1 stale
 *   8      8    uint64 LE seq              monotonic per connection, starts at 1
 *  16      8    int64 LE  engine_ts_ms     server time the quote was stamped
 *  24      8    int64 LE  recv_ts_ms       when the gateway received it
 *  32      8    int64 LE  bid_e8           bid x 1e8
 *  40      8    int64 LE  ask_e8           ask x 1e8
 *  48      8    int64 LE  bid_volume_e4    lots x 1e4 (0 = unknown)
 *  56      8    int64 LE  ask_volume_e4    lots x 1e4 (0 = unknown)
 *  64     20    ASCII     symbol           NUL padded, e.g. "EURUSD" + 14 NULs
 *  84     10    ASCII     source           NUL padded, e.g. "SIMULATED"
 *  94      2    uint16 LE reserved         always 0; reject if non-zero
 * ```
 *
 * Rules a decoder must implement:
 *
 * - All integers are **little-endian two's complement**. `Date.now()` fits in
 *   `engine_ts_ms`/`recv_ts_ms`; the `e8`/`e4` fields are the same scaled
 *   integers the JSON control channel uses, so a client decodes into a bigint
 *   (`BigInt.asIntN(64, …)`), never a double.
 * - `magic`, `version` and `header_bytes` must match, or the frame is refused.
 *   A client that cannot speak the version is refused with an explicit upgrade
 *   message rather than silently degraded (doc 01 §5).
 * - `flags` bit1 (`stale`) means the gateway is forwarding a quote that is
 *   older than the configured maximum. Trading clients must not use a stale
 *   frame as a mark; the trade service will refuse such an order with
 *   `E_NO_QUOTE` anyway.
 * - `symbol`/`source` are ASCII and NUL padded; a non-ASCII byte is a protocol
 *   error, not a symbol.
 *
 * The frame deliberately carries **no** tenant or account id: a market-data
 * connection is already bound to a session (and therefore a tenant) at the
 * edge, and repeating it 20 times a second is pure bandwidth.
 */

import type { Price, Volume } from '@weke/domain';

export const QUOTE_FRAME_VERSION = 1;
export const QUOTE_FRAME_BYTES = 96;
export const QUOTE_FRAME_MAGIC = 0x4b57;
export const QUOTE_FRAME_HEADER_BYTES = 96;
export const SYMBOL_FIELD_BYTES = 20;
export const SOURCE_FIELD_BYTES = 10;

export const QUOTE_FLAG_SESSION_OPEN = 0b0001;
export const QUOTE_FLAG_STALE = 0b0010;

export const FrameType = { QUOTE: 1, HEARTBEAT: 2 } as const;
export type FrameType = (typeof FrameType)[keyof typeof FrameType];

/** One quote, in the units the wire uses (already scaled, no floats). */
export interface QuoteFrame {
  readonly frameType: FrameType;
  readonly seq: bigint;
  readonly engineTsMs: bigint;
  readonly recvTsMs: bigint;
  readonly bid: Price;
  readonly ask: Price;
  readonly bidVolume: Volume;
  readonly askVolume: Volume;
  readonly symbol: string;
  readonly source: string;
  readonly sessionOpen: boolean;
  readonly stale: boolean;
}

export class QuoteFrameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'QuoteFrameError';
  }
}

function writeAscii(buffer: Buffer, offset: number, length: number, value: string, field: string): void {
  if (value.length === 0) throw new QuoteFrameError(`${field} must not be empty`);
  if (value.length > length) {
    throw new QuoteFrameError(`${field} ${JSON.stringify(value)} is longer than ${length} bytes`);
  }
  if (!/^[\x20-\x7e]+$/.test(value)) {
    throw new QuoteFrameError(`${field} ${JSON.stringify(value)} is not printable ASCII`);
  }
  buffer.write(value, offset, length, 'ascii');
}

function readAscii(buffer: Buffer, offset: number, length: number, field: string): string {
  let end = offset;
  while (end < offset + length && buffer[end] !== 0) end += 1;
  for (let index = offset; index < end; index += 1) {
    const byte = buffer[index] ?? 0;
    if (byte < 0x20 || byte > 0x7e) {
      throw new QuoteFrameError(`${field} contains a non-printable byte`);
    }
  }
  // Everything after the first NUL must be padding, not data.
  for (let index = end; index < offset + length; index += 1) {
    if (buffer[index] !== 0) throw new QuoteFrameError(`${field} has data after its NUL padding`);
  }
  return buffer.toString('ascii', offset, end);
}

function checkInt64(value: bigint, field: string): bigint {
  if (value < -(2n ** 63n) || value > 2n ** 63n - 1n) {
    throw new QuoteFrameError(`${field} is outside int64: ${value.toString()}`);
  }
  return value;
}

/** Encode one quote into its 96-byte wire form. */
export function encodeQuoteFrame(quote: QuoteFrame): Buffer {
  const buffer = Buffer.alloc(QUOTE_FRAME_BYTES);
  buffer.writeUInt16LE(QUOTE_FRAME_MAGIC, 0);
  buffer.writeUInt8(QUOTE_FRAME_VERSION, 2);
  buffer.writeUInt8(quote.frameType, 3);
  buffer.writeUInt16LE(QUOTE_FRAME_HEADER_BYTES, 4);
  const flags =
    (quote.sessionOpen ? QUOTE_FLAG_SESSION_OPEN : 0) | (quote.stale ? QUOTE_FLAG_STALE : 0);
  buffer.writeUInt16LE(flags, 6);
  buffer.writeBigUInt64LE(BigInt.asUintN(64, quote.seq), 8);
  buffer.writeBigInt64LE(checkInt64(quote.engineTsMs, 'engineTsMs'), 16);
  buffer.writeBigInt64LE(checkInt64(quote.recvTsMs, 'recvTsMs'), 24);
  buffer.writeBigInt64LE(checkInt64(quote.bid, 'bid'), 32);
  buffer.writeBigInt64LE(checkInt64(quote.ask, 'ask'), 40);
  buffer.writeBigInt64LE(checkInt64(quote.bidVolume, 'bidVolume'), 48);
  buffer.writeBigInt64LE(checkInt64(quote.askVolume, 'askVolume'), 56);
  writeAscii(buffer, 64, SYMBOL_FIELD_BYTES, quote.symbol, 'symbol');
  writeAscii(buffer, 84, SOURCE_FIELD_BYTES, quote.source, 'source');
  buffer.writeUInt16LE(0, 94);
  return buffer;
}

/** Decode one 96-byte frame. Throws `QuoteFrameError` on any deviation. */
export function decodeQuoteFrame(bytes: Buffer): QuoteFrame {
  if (bytes.length !== QUOTE_FRAME_BYTES) {
    throw new QuoteFrameError(
      `frame is ${bytes.length} bytes, expected exactly ${QUOTE_FRAME_BYTES}`,
    );
  }
  const magic = bytes.readUInt16LE(0);
  if (magic !== QUOTE_FRAME_MAGIC) {
    throw new QuoteFrameError(`bad magic 0x${magic.toString(16)}`);
  }
  const version = bytes.readUInt8(2);
  if (version !== QUOTE_FRAME_VERSION) {
    throw new QuoteFrameError(
      `unsupported market-data version ${version}; this client speaks ${QUOTE_FRAME_VERSION}`,
    );
  }
  const frameType = bytes.readUInt8(3) as FrameType;
  if (frameType !== FrameType.QUOTE && frameType !== FrameType.HEARTBEAT) {
    throw new QuoteFrameError(`unknown frame type ${frameType}`);
  }
  const headerBytes = bytes.readUInt16LE(4);
  if (headerBytes !== QUOTE_FRAME_HEADER_BYTES) {
    throw new QuoteFrameError(`header_bytes ${headerBytes} != ${QUOTE_FRAME_HEADER_BYTES}`);
  }
  const flags = bytes.readUInt16LE(6);
  const reserved = bytes.readUInt16LE(94);
  if (reserved !== 0) throw new QuoteFrameError(`reserved word is ${reserved}, expected 0`);
  return {
    frameType,
    seq: bytes.readBigUInt64LE(8),
    engineTsMs: bytes.readBigInt64LE(16),
    recvTsMs: bytes.readBigInt64LE(24),
    bid: bytes.readBigInt64LE(32) as Price,
    ask: bytes.readBigInt64LE(40) as Price,
    bidVolume: bytes.readBigInt64LE(48) as Volume,
    askVolume: bytes.readBigInt64LE(56) as Volume,
    symbol: readAscii(bytes, 64, SYMBOL_FIELD_BYTES, 'symbol'),
    source: readAscii(bytes, 84, SOURCE_FIELD_BYTES, 'source'),
    sessionOpen: (flags & QUOTE_FLAG_SESSION_OPEN) !== 0,
    stale: (flags & QUOTE_FLAG_STALE) !== 0,
  };
}

